import React, { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { CircularProgress } from "@material-ui/core";
import DashboardCard from "./cards/DashboardCard";
import { apiReports } from "../api/reports";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const green = "#94ba33";
const blue = "#00acdc";
const red = "#d71433";
const amber = "rgba(251,191,36)";

// Parse a "YYYY-MM-DD" string as a UTC day and format for axis display.
const fmtDay = (iso) => {
  if (!iso) {return iso;}
  const d = new Date(iso + "T00:00:00Z");
  if (isNaN(d)) {return iso;}
  return `${d.getUTCDate()} ${MONTH_NAMES[d.getUTCMonth()]}`;
};

const todayKey = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
};

const barOptions = (yLabel) => ({
  maintainAspectRatio: false,
  legend: { display: false },
  scales: {
    xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 12 } }],
    yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: yLabel } }],
  },
  tooltips: { mode: "index", intersect: false },
});

// Fill a sparse {date: count} series into a contiguous 30-day array ending today.
function fill30Days(points) {
  const byDay = {};
  (points || []).forEach((p) => { byDay[p.date] = p.count; });
  const out = [];
  const labels = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
    out.push(byDay[key] || 0);
    labels.push(fmtDay(key));
  }
  return { labels, data: out };
}

const CompletedTab = () => {
  const [resp, setResp] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiReports.getCompletedStats()
      .then((res) => { if (alive) { setResp(res); setLoading(false); } })
      .catch((err) => { if (alive) { setError(err); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const r = resp?.data?.result;

  // Month-to-date window and days remaining to month end (UTC).
  const monthStart = useMemo(() => {
    const d = new Date();
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
  }, []);

  const daysToEom = useMemo(() => {
    const d = new Date();
    const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
    // Count today as a remaining (partial) day: on the last day this is 1, so
    // the projections don't just freeze at the morning's totals.
    return lastDay - d.getUTCDate() + 1;
  }, []);

  const sumSince = (points, fromKey) =>
    (points || []).reduce((s, p) => p.date >= fromKey ? s + p.count : s, 0);

  const sumLast = (points, days) => {
    const today = new Date();
    const cutoff = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - (days - 1));
    return (points || []).reduce((s, p) => {
      const d = new Date(p.date + "T00:00:00Z").getTime();
      return d >= cutoff ? s + p.count : s;
    }, 0);
  };

  // Completed throughput + projection.
  const completed7 = useMemo(() => sumLast(r?.completedPerDay, 7), [r]);
  const completed30 = useMemo(() => sumLast(r?.completedPerDay, 30), [r]);
  const completedSoFarMTD = useMemo(() => sumSince(r?.completedPerDay, monthStart), [r, monthStart]);
  const completedRate7d = completed7 / 7;
  const completedRate30d = completed30 / 30;
  const completedProjectedEom7d = completedSoFarMTD + Math.round(completedRate7d * daysToEom);
  const completedProjectedEom30d = completedSoFarMTD + Math.round(completedRate30d * daysToEom);

  // Submitted throughput + projection.
  const submitted7 = useMemo(() => sumLast(r?.submittedPerDay, 7), [r]);
  const submittedSoFarMTD = useMemo(() => sumSince(r?.submittedPerDay, monthStart), [r, monthStart]);
  const submittedRate7d = submitted7 / 7;
  const submittedProjectedEom7d = submittedSoFarMTD + Math.round(submittedRate7d * daysToEom);

  // Waiting-to-submit queue drain time at the 7-day submission rate (fractional days).
  const queueDrainDays = submittedRate7d > 0 ? (r?.waitingToSubmit ?? 0) / submittedRate7d : null;
  const queueDrainLabel = queueDrainDays === null
    ? '---'
    : queueDrainDays === 0 ? '0 days'
      : queueDrainDays < 1 ? '<1 day'
        : `${Math.round(queueDrainDays)} days`;

  const completedToday = useMemo(() => {
    const key = todayKey();
    const found = (r?.completedPerDay || []).find((p) => p.date === key);
    return found ? found.count : 0;
  }, [r]);

  const submittedToday = useMemo(() => {
    const key = todayKey();
    const found = (r?.submittedPerDay || []).find((p) => p.date === key);
    return found ? found.count : 0;
  }, [r]);

  const completedChart = useMemo(() => {
    const f = fill30Days(r?.completedPerDay);
    return {
      labels: f.labels,
      datasets: [{ label: "Completed", data: f.data, backgroundColor: green, borderColor: green, borderWidth: 1 }],
    };
  }, [r]);

  const submittedChart = useMemo(() => {
    const f = fill30Days(r?.submittedPerDay);
    return {
      labels: f.labels,
      datasets: [{ label: "Submitted", data: f.data, backgroundColor: blue, borderColor: blue, borderWidth: 1 }],
    };
  }, [r]);

  const agingChart = useMemo(() => {
    const aging = r?.waitingAging || [];
    return {
      labels: aging.map((b) => b.bucket),
      datasets: [{ label: "Waiting", data: aging.map((b) => b.count), backgroundColor: red, borderColor: red, borderWidth: 1 }],
    };
  }, [r]);

  const lagChart = useMemo(() => {
    const lag = r?.completedSubmitLag || [];
    return {
      labels: lag.map((b) => b.bucket),
      datasets: [{ label: "Inspections", data: lag.map((b) => b.count), backgroundColor: amber, borderColor: "#eab308", borderWidth: 1 }],
    };
  }, [r]);

  if (loading) {
    return <div className="tw-flex tw-justify-center tw-py-12"><CircularProgress /></div>;
  }
  if (error) {
    return <div className="tw-text-red-600 tw-py-8">Unable to load completed stats.</div>;
  }

  return (
    <>
      {/* Current-state headlines: today's snapshot + queue size.
          Month-to-date, rates, and EOM projections live in the projection box below. */}
      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-mt-6 tw-mb-8">
        <DashboardCard title="Waiting to submit" value={r?.waitingToSubmit ?? 0} color="tw-bg-fs-red" />
        <DashboardCard title="Completed today" value={completedToday} color="tw-bg-fs-green" />
        <DashboardCard title="Submitted today" value={submittedToday} color="tw-bg-primary" />
      </div>

      {/* Completed & submitted projection to end of month */}
      {r &&
        <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-6 tw-mb-8">
          <h4 className="tw-text-lg tw-font-semibold tw-mb-1">Completed projection &mdash; end of month</h4>
          <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
            Completions and submissions this month, projected to month end at the current 7-day and 30-day rates. Queue drain is the waiting-to-submit backlog at the 7-day submission rate.
          </p>
          <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 xl:tw-grid-cols-8 tw-gap-4 md:tw-gap-6">
            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
              <p className="tw-text-xs tw-uppercase tw-text-gray-500">Completed so far</p>
              <p className="tw-text-2xl tw-font-bold">{completedSoFarMTD}</p>
            </div>
            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
              <p className="tw-text-xs tw-uppercase tw-text-gray-500">Rate (7-day)</p>
              <p className="tw-text-2xl tw-font-bold tw-text-primary">{Math.round(completedRate7d)}/day</p>
            </div>
            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
              <p className="tw-text-xs tw-uppercase tw-text-gray-500">Rate (30-day)</p>
              <p className="tw-text-2xl tw-font-bold tw-text-primary">{Math.round(completedRate30d)}/day</p>
            </div>
            <div className="tw-bg-fs-dark-blue tw-bg-opacity-10 tw-rounded-lg tw-p-3">
              <p className="tw-text-xs tw-uppercase tw-text-gray-500">Completed projected by EOM (7-day)</p>
              <p className="tw-text-2xl tw-font-bold tw-text-primary">{completedProjectedEom7d}</p>
            </div>
            <div className="tw-bg-fs-dark-blue tw-bg-opacity-10 tw-rounded-lg tw-p-3">
              <p className="tw-text-xs tw-uppercase tw-text-gray-500">Completed projected by EOM (30-day)</p>
              <p className="tw-text-2xl tw-font-bold tw-text-primary">{completedProjectedEom30d}</p>
            </div>
            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
              <p className="tw-text-xs tw-uppercase tw-text-gray-500">Submitted so far</p>
              <p className="tw-text-2xl tw-font-bold">{submittedSoFarMTD}</p>
            </div>
            <div className="tw-bg-fs-dark-blue tw-bg-opacity-10 tw-rounded-lg tw-p-3">
              <p className="tw-text-xs tw-uppercase tw-text-gray-500">Submitted projected by EOM (7-day)</p>
              <p className="tw-text-2xl tw-font-bold tw-text-primary">{submittedProjectedEom7d}</p>
            </div>
            <div className="tw-bg-fs-red tw-bg-opacity-10 tw-rounded-lg tw-p-3">
              <p className="tw-text-xs tw-uppercase tw-text-gray-500">Queue drain (at 7-day rate)</p>
              <p className="tw-text-2xl tw-font-bold tw-text-fs-red">{queueDrainLabel}</p>
            </div>
          </div>
        </div>
      }

      {/* Waiting-to-submit queue + aging */}
      <h4 className="tw-text-xl tw-font-bold tw-mt-6 tw-mb-2">Waiting to submit</h4>
      <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
        Inspections marked completed but not yet submitted, by age since completion. (Future: &ldquo;Awaiting QA&rdquo; and &ldquo;Problem&rdquo; sub-statuses will split this queue.)
      </p>
      <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4 tw-mb-10" style={{ height: "280px" }}>
        {agingChart.datasets[0].data.some((v) => v > 0) ?
          <Bar data={agingChart} options={barOptions("Inspections")} />
          :
          <p className="tw-text-gray-500 tw-text-sm">Nothing waiting to submit.</p>
        }
      </div>

      {/* Completed per day */}
      <h4 className="tw-text-xl tw-font-bold tw-mt-10 tw-mb-2">Completed per day (last 30 days)</h4>
      <hr className="tw-border-primary tw-mb-6" />
      <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4 tw-mb-10" style={{ height: "320px" }}>
        <Bar data={completedChart} options={barOptions("Inspections completed")} />
      </div>

      {/* Submitted per day */}
      <h4 className="tw-text-xl tw-font-bold tw-mt-10 tw-mb-2">Submitted per day (last 30 days)</h4>
      <hr className="tw-border-primary tw-mb-6" />
      <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4 tw-mb-10" style={{ height: "320px" }}>
        <Bar data={submittedChart} options={barOptions("Inspections submitted")} />
      </div>

      {/* Completed -> submit lag */}
      <h4 className="tw-text-xl tw-font-bold tw-mt-10 tw-mb-2">Completed &rarr; Submitted lag (last 30 days)</h4>
      <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
        Days between completion and submission for inspections submitted in the last 30 days.
      </p>
      <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4 tw-mb-10" style={{ height: "280px" }}>
        {lagChart.datasets[0].data.some((v) => v > 0) ?
          <Bar data={lagChart} options={barOptions("Inspections")} />
          :
          <p className="tw-text-gray-500 tw-text-sm">No data.</p>
        }
      </div>
    </>
  );
};

export default CompletedTab;